import { AxiosError } from 'axios'
import { computed, flow, makeObservable, observable } from 'mobx'

import { openDocumentFromBuffer, requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, LoadingBtnNames } from 'Core/interfaces'
import {
	emptyCustomLabelValue,
	entityToLabelValue,
	entityToLabelWithDashValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import {
	getCancelInvoiceSuccessMessage,
	getUploadBackupDocumentSuccessMessage,
	getUploadInvoiceSuccessMessage
} from 'Portals/Supplier/constants'
import { Invoice, InvoiceStatusLabel } from 'Portals/Supplier/entities'
import { InvoiceRepo } from 'Portals/Supplier/repositories'
import { RootStore } from 'Portals/Supplier/stores'
import { RouteBuilder } from 'Portals/Supplier/utils'
import i18n from 'Portals/Supplier/utils/i18nService'

import { InvoiceTable } from './InvoiceTable'

export class InvoiceStore {
	table: InvoiceTable
	rootStore: RootStore
	@observable accessor supplierLocations = getDefaultStoreArrayValue()
	@observable accessor invoice = getDefaultStoreValue<Invoice.Edit.Basic>()
	@observable accessor shortPays = getDefaultStoreArrayValue<Invoice.Edit.ShortPays>()
	@observable accessor backupDocuments = getDefaultStoreArrayValue<Invoice.Edit.BackupDocument.Basic>()
	@observable accessor documentTypes = getDefaultStoreArrayValue<Entity>()

	@observable accessor error: AxiosError | null = null

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
		this.table = new InvoiceTable(rootStore)
	}

	@computed
	get supplierLocationsAllOptions() {
		return [
			emptyCustomLabelValue(i18n.t('core:labels.all')),
			...this.supplierLocations.value.map(entityToLabelWithDashValue)
		]
	}

	@computed
	get supplierLocationsOptions() {
		return this.supplierLocations.value.map(entityToLabelWithDashValue)
	}

	@computed
	get documentTypesOptions() {
		return this.documentTypes.value.map(entityToLabelValue)
	}

	@computed
	get isSentOrInProgress() {
		const status = this.invoice.value?.status.toLowerCase()
		return status === InvoiceStatusLabel.inProgress || status === InvoiceStatusLabel.sent
	}

	@computed
	get isShortPays() {
		return !!this.shortPays.value.length
	}

	@flow.bound
	*downloadInvoiceDocument(invoiceId: number, documentId: number): CustomGenerator<ArrayBuffer> {
		try {
			const document = yield InvoiceRepo.downloadInvoiceDocument(invoiceId, documentId)
			openDocumentFromBuffer(document)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*downloadBackupDocument(documentId: number): CustomGenerator<ArrayBuffer> {
		if (!this.invoice.value?.invoiceId) {
			return
		}
		const invoiceId = this.invoice.value?.invoiceId
		try {
			const document = yield InvoiceRepo.downloadBackupDocument(invoiceId, documentId)
			openDocumentFromBuffer(document)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*removeDocument(documentId: number) {
		if (!this.invoice.value?.invoiceId) {
			return
		}
		const invoiceId = this.invoice.value?.invoiceId
		try {
			yield InvoiceRepo.removeDocument(invoiceId, documentId)
			this.retrieveBackupDocument(invoiceId.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveSupplierLocations() {
		yield requestWrapper({
			field: this.supplierLocations,
			repository: InvoiceRepo.retrieveSupplierLocations,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDocumentTypes() {
		yield requestWrapper({
			field: this.documentTypes,
			repository: InvoiceRepo.retrieveDocumentTypes,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveInvoiceData(invoiceId: string) {
		yield this.retrieveInvoiceById(invoiceId)
		this.retrieveShortPays(invoiceId)
		this.retrieveBackupDocument(invoiceId)
	}

	@flow.bound
	*retrieveInvoiceById(id: Key): CustomGenerator<Invoice.Edit.Basic> {
		yield requestWrapper({
			field: this.invoice,
			repository: InvoiceRepo.retrieveInvoiceById,
			repositoryParams: id
		})
	}

	@flow.bound
	*retrieveShortPays(id: string): CustomGenerator<Invoice.Edit.ShortPays[]> {
		this.shortPays.isLoading = true
		try {
			const shortPays = yield InvoiceRepo.retrieveShortPays(id)
			const invoice = this.invoice.value

			if (!invoice) {
				return
			}
			const { shortPayReason, invoiceDate, invoiceCost, shortPayCost } = invoice

			const firstRow: Invoice.Edit.ShortPays = {
				itemDescription: 'Invoice',
				quantity: null,
				pounds: null,
				serviceDate: invoiceDate,
				invoiceCost,
				shortPayReason,
				shortPayCost
			}
			this.shortPays.value = shortPayReason ? [firstRow, ...shortPays] : shortPays
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.shortPays.isLoading = false
		}
	}

	@flow.bound
	*cancelInvoice() {
		if (!this.invoice.value?.invoiceId) {
			return
		}
		const invoiceId = this.invoice.value?.invoiceId
		this.rootStore[STORE_UI].setIsRequestSubmitting()
		try {
			yield InvoiceRepo.cancelInvoice(invoiceId)
			this.rootStore[STORE_UI].showSuccessNotification(getCancelInvoiceSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveInvoiceById(invoiceId)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*uploadInvoice(data: Invoice.Add.Request.Add, isContinue?: boolean): CustomGenerator<number> {
		this.rootStore[STORE_UI].setIsBtnLoading(
			isContinue ? LoadingBtnNames.uploadInvoiceCont : LoadingBtnNames.uploadInvoiceAdd,
			true
		)
		try {
			const invoiceId = yield InvoiceRepo.uploadInvoice(data)
			this.rootStore[STORE_UI].showSuccessNotification(getUploadInvoiceSuccessMessage())
			if (isContinue && invoiceId) {
				this.rootStore.history.push(RouteBuilder.invoice.add(invoiceId).build())
			} else {
				this.table.getItems()
			}
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@flow.bound
	*retrieveBackupDocument(id: string): CustomGenerator<Invoice.Edit.BackupDocument.Basic> {
		yield requestWrapper({
			field: this.backupDocuments,
			repository: InvoiceRepo.retrieveBackupDocument,
			repositoryParams: id
		})
	}

	@flow.bound
	*uploadBackupDocument(data: Invoice.Edit.BackupDocument.Request.Add): CustomGenerator<void> {
		if (!this.invoice.value?.invoiceId) {
			return
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting()

		const invoiceId = this.invoice.value?.invoiceId
		try {
			yield InvoiceRepo.uploadBackupDocument({
				id: invoiceId,
				data
			})
			this.rootStore[STORE_UI].showSuccessNotification(
				getUploadBackupDocumentSuccessMessage()
			)
			this.rootStore[STORE_UI].closeModal()
			this.retrieveBackupDocument(invoiceId.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}
}
